import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { LogoComponent } from './logo';
import { SocialIconComponent } from './social-icon';
import { I18nService } from '../core/i18n/i18n.service';
import { ContentService } from '../core/content.service';
import { TPipe } from '../core/i18n/t.pipe';

@Component({
  selector: 'se-footer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, LogoComponent, SocialIconComponent, TPipe],
  template: `
    <footer class="bg-ink text-white">
      <div class="container-page py-20">
        <div class="grid gap-14 md:grid-cols-[1.4fr_1fr_1fr]">
          <!-- Brand + CTA -->
          <div>
            <se-logo tone="light" size="2rem" />
            <p class="mt-5 max-w-xs text-sm leading-relaxed text-white/60">
              {{ 'footer.tagline' | t }}
            </p>
            <a
              routerLink="/contact"
              class="group mt-8 inline-flex items-center gap-3 text-2xl font-bold tracking-tight"
            >
              {{ 'footer.cta' | t }}
              <span
                class="grid h-9 w-9 place-items-center rounded-full bg-brand-500 transition-transform group-hover:translate-x-1"
                [class.rotate-180]="i18n.isRtl()"
              >→</span>
            </a>
          </div>

          <!-- Nav -->
          <div>
            <h3 class="text-xs uppercase tracking-[0.3em] text-white/40">
              {{ 'footer.nav' | t }}
            </h3>
            <ul class="mt-5 space-y-3 text-sm">
              @for (l of links; track l.path) {
                <li>
                  <a
                    [routerLink]="l.path"
                    class="link-underline text-white/75 hover:text-white"
                    >{{ l.key | t }}</a
                  >
                </li>
              }
            </ul>
          </div>

          <!-- Contact -->
          <div>
            <h3 class="text-xs uppercase tracking-[0.3em] text-white/40">
              {{ 'footer.contact' | t }}
            </h3>
            <ul class="mt-5 space-y-3 text-sm">
              <li>
                <a
                  href="mailto:info&#64;se.agency"
                  class="link-underline text-white/75 hover:text-white"
                  >info&#64;se.agency</a
                >
              </li>
              <li class="text-white/50">{{ 'contact.hours' | t }}</li>
            </ul>
            <div class="mt-5 flex gap-3">
              @for (s of socials; track s.href) {
                <a
                  [href]="s.href"
                  target="_blank"
                  rel="noopener"
                  [attr.aria-label]="s.label"
                  class="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-white hover:bg-white hover:text-ink"
                >
                  <se-social-icon [name]="s.key" />
                </a>
              }
            </div>
          </div>
        </div>

        <div
          class="mt-16 flex flex-col gap-4 border-t border-white/10 pt-8 text-xs text-white/40 sm:flex-row sm:items-center sm:justify-between"
        >
          <p>© {{ year }} SE.AGENCY — {{ 'footer.rights' | t }}</p>
          <div class="flex items-center gap-4">
            <a [routerLink]="'/' + privacySlug()" class="link-underline text-white/60 hover:text-white">
              {{ 'footer.privacy' | t }}
            </a>
            <span class="tracking-wide">Design · Development · Marketing</span>
          </div>
        </div>
      </div>
    </footer>
  `,
})
export class FooterComponent {
  protected i18n = inject(I18nService);
  private content = inject(ContentService);
  protected year = new Date().getFullYear();

  /** Admin-editable privacy URL slug (defaults to "privacy"). */
  protected privacySlug = toSignal(
    this.content.getPolicy().pipe(map((p) => p.slug || 'privacy')),
    { initialValue: 'privacy' },
  );
  protected links = [
    { path: '/', key: 'nav.home' },
    { path: '/about', key: 'nav.about' },
    { path: '/work', key: 'nav.work' },
    { path: '/services', key: 'nav.services' },
    { path: '/contact', key: 'nav.contact' },
  ];

  protected socials = SOCIALS;
}

/** Shared social profiles (used by footer + contact page). */
export const SOCIALS = [
  { label: 'Instagram', key: 'instagram', href: 'https://www.instagram.com/se.agency.eg/' },
  { label: 'Facebook', key: 'facebook', href: 'https://www.facebook.com/www.se.agency' },
  { label: 'LinkedIn', key: 'linkedin', href: 'https://www.linkedin.com/company/seagency-eg/' },
];
